/**
 * Progressi salvati in localStorage. Ogni accesso è difensivo: in modalità
 * privata o con i cookie bloccati il gioco deve comunque partire.
 */

const KEY = 'cat-bastard/progress/v1';
const SETTINGS_KEY = 'cat-bastard/settings/v1';
const SESSION_KEY = 'cat-bastard/session/v1';

export interface LevelRecord {
  cleared: boolean;
  bestDeaths: number;
  bestTicks: number;
  bestCoins: number;
}

export interface Progress {
  levels: Record<string, LevelRecord>;
  totalDeaths: number;
  /**
   * Id dei livelli in cui il gomitolo nascosto è stato trovato.
   *
   * Sta nei progressi e non nel record del livello perché un segreto trovato
   * non si perde più: rigiocare il livello e non prenderlo non lo cancella —
   * sarebbe l'unica cattiveria del gioco senza una battuta a giustificarla.
   */
  secrets: string[];
  /**
   * Le imprese compiute (vedi `game/feats.ts`).
   *
   * Sono le marche degli easter egg: il codice digitato, la mezz'ora da fermo,
   * le morti collezionate. Stanno accanto ai gomitoli e si comportano come
   * loro — una cosa fatta non si disfa, nemmeno cambiando computer — perché
   * anche loro sbloccano un gatto, e un gatto che sparisce è peggio di un gatto
   * che non c'è mai stato.
   */
  feats: string[];
}

const emptyProgress = (): Progress => ({ levels: {}, totalDeaths: 0, secrets: [], feats: [] });

export function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return emptyProgress();
    const parsed = JSON.parse(raw) as Partial<Progress>;
    // Ogni campo ha il suo default: un salvataggio di una versione precedente
    // del gioco deve continuare a caricarsi, non azzerare i record di nessuno.
    return {
      levels: parsed.levels ?? {},
      totalDeaths: parsed.totalDeaths ?? 0,
      // Chi giocava prima che i gomitoli esistessero non ne ha nessuno, e va
      // benissimo così: il salvataggio vecchio deve continuare a caricarsi.
      secrets: parsed.secrets ?? [],
      feats: parsed.feats ?? [],
    };
  } catch {
    return emptyProgress();
  }
}

/** Preferenze del giocatore. Poche, e tutte con un default sensato. */
export interface Settings {
  audio: boolean;
  /** Id del gatto scelto (vedi game/cats.ts). */
  cat: string;
  /**
   * Il popup dell'account è già stato mostrato una volta.
   *
   * Serve a non riproporlo a ogni apertura: chi ha risposto "non ora" ha
   * risposto, e continuare a chiederglielo sarebbe l'unica cosa del gioco a
   * dargli fastidio senza essere una trappola.
   */
  accountPromptSeen: boolean;
}

const defaultSettings = (): Settings => ({
  audio: true,
  cat: 'bastardo',
  accountPromptSeen: false,
});

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return defaultSettings();
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      audio: parsed.audio ?? true,
      cat: parsed.cat ?? 'bastardo',
      accountPromptSeen: parsed.accountPromptSeen ?? false,
    };
  } catch {
    return defaultSettings();
  }
}

/**
 * La sessione dell'account, se c'è.
 *
 * Il token è una stringa casuale generata dal server: qui si conserva com'è,
 * nel database ne esiste solo l'impronta. Non contiene niente — non è un JWT,
 * non ha dentro il nickname, non scade da solo — ed è esattamente quello che
 * serve: senza il database non vale niente.
 */
export interface Session {
  token: string;
  nickname: string;
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (typeof parsed.token !== 'string' || typeof parsed.nickname !== 'string') return null;
    if (!parsed.token) return null;
    return { token: parsed.token, nickname: parsed.nickname };
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Senza salvataggio si resta connessi finché la pagina è aperta. Poco, ma
    // meglio di un errore in faccia a chi gioca in navigazione privata.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Come sopra.
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Come sopra: senza salvataggio si gioca lo stesso.
  }
}

/** Cancella tutto: record, livelli sbloccati, morti accumulate. */
export function resetProgress(): Progress {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Se non si può cancellare, almeno la sessione riparte pulita.
  }
  return emptyProgress();
}

export function saveProgress(progress: Progress): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(progress));
  } catch {
    // Niente salvataggio: pazienza, non è un motivo per rompere il gioco.
  }
}

/** Registra un livello completato tenendo solo il risultato migliore. */
export function recordClear(
  progress: Progress,
  levelId: string,
  run: { deaths: number; ticks: number; coins: number },
): Progress {
  const previous = progress.levels[levelId];
  const next: LevelRecord = {
    cleared: true,
    bestDeaths: previous ? Math.min(previous.bestDeaths, run.deaths) : run.deaths,
    bestTicks: previous ? Math.min(previous.bestTicks, run.ticks) : run.ticks,
    bestCoins: previous ? Math.max(previous.bestCoins, run.coins) : run.coins,
  };
  const updated: Progress = {
    ...progress,
    levels: { ...progress.levels, [levelId]: next },
    totalDeaths: progress.totalDeaths + run.deaths,
  };
  saveProgress(updated);
  return updated;
}

/** Segna il gomitolo di un livello come trovato. Idempotente. */
export function recordSecret(progress: Progress, levelId: string): Progress {
  if (progress.secrets.includes(levelId)) return progress;
  const updated: Progress = { ...progress, secrets: [...progress.secrets, levelId] };
  saveProgress(updated);
  return updated;
}

/**
 * Segna un'impresa come compiuta. Idempotente, come i gomitoli.
 *
 * Restituisce lo stesso oggetto se non c'era niente da segnare: chi chiama può
 * confrontare i due riferimenti per sapere se è successo qualcosa di nuovo, ed
 * è così che il gioco decide se annunciare un gatto o stare zitto.
 */
export function recordFeat(progress: Progress, featId: string): Progress {
  if (progress.feats.includes(featId)) return progress;
  const updated: Progress = { ...progress, feats: [...progress.feats, featId] };
  saveProgress(updated);
  return updated;
}
